import { ProductInformation, ProductTypeName } from './product-information.model';
import { ProductCode } from './product.model';

/**
 * Pseudo-factory function to create product information with localized names.
 * Returns a [key, value] tuple that can be spread into an object.
 * Reuses the same name across all locales (en, de, fr).
 */
const createProductInformation = <T extends ProductCode>(
  type: T,
  displayName: string,
  publicationDays: number
): readonly [T, ProductInformation<T>] => [
  type,
  {
    type,
    publicationDays,
    name: {
      en: displayName,
      de: displayName,
      fr: displayName,
    } as ProductTypeName,
  } as const,
];

type ProductInformationRegistry = {
  readonly [K in (typeof supportedProductCodes)[number]]: ProductInformation<K>;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- consumed only via typeof in ProductInformationRegistry above
const supportedProductCodes = [
  ProductCode.PAYPERPERFORMANCE,
  ProductCode.JOBSSPONTANEOUSAPPLICATION,
  ProductCode.JOBUPSPONTANEOUSAPPLICATION,
  ProductCode.JOBSOFFSITE,
  ProductCode.JOBUPOFFSITE,
  ProductCode.JOBSGIFTEDFREE,
  ProductCode.JOBUPGIFTEDFREE,
] as const;

/**
 * Registry mapping product codes to their corresponding offline product information.
 * Those products are not fetched from the ecom but are needed in various places.
 * E.g., when creating credits for those products.
 */
// prettier-ignore
export const productInformationRegistry: ProductInformationRegistry = Object.fromEntries([
  createProductInformation(ProductCode.PAYPERPERFORMANCE, 'Pay Per Performance', 10_000),
  createProductInformation(ProductCode.JOBSSPONTANEOUSAPPLICATION, 'jobs.ch Spontaneous Application', 365),
  createProductInformation(ProductCode.JOBUPSPONTANEOUSAPPLICATION, 'jobup.ch Spontaneous Application', 365),
  createProductInformation(ProductCode.JOBSOFFSITE, 'jobs.ch Offsite Publication', 30),
  createProductInformation(ProductCode.JOBUPOFFSITE, 'jobup.ch Offsite Publication', 30),
  createProductInformation(ProductCode.JOBSGIFTEDFREE, 'jobs.ch Gifted Free', 10),
  createProductInformation(ProductCode.JOBUPGIFTEDFREE, 'jobup.ch Gifted Free', 10),
]) as ProductInformationRegistry;
